export type StatisticsRange = "week" | "fortnight" | "month" | "all"

export const RANGES: readonly { value: StatisticsRange; label: string }[] = [
  { value: "week", label: "Senaste veckan" },
  { value: "fortnight", label: "14 dagar" },
  { value: "month", label: "Månaden" },
  { value: "all", label: "Sedan start" },
] as const

export const DEFAULT_RANGE: StatisticsRange = "fortnight"

export function isRange(value: unknown): value is StatisticsRange {
  return RANGES.some((r) => r.value === value)
}

// --- Sleep bars chart geometry ---
export const BARS = {
  plotHeight: 200,
  topPad: 12,
  bottomPad: 26, // room for x-axis day labels
  leftAxis: 34,
} as const

export function barsSvgHeight(): number {
  return BARS.topPad + BARS.plotHeight + BARS.bottomPad
}

// Fixed viewBox width; rendered w-full so the plot never scrolls horizontally.
export const PLOT = { width: 300 } as const

export function pointX(index: number, count: number): number {
  if (count <= 0) return 0
  return ((index + 0.5) * PLOT.width) / count
}

// Even-hour y-axis ceiling clearing the tallest bar and the recommended band.
export function niceMaxHours(maxDailyMinutes: number, recommendedMaxHours: number): number {
  const raw = Math.max(maxDailyMinutes / 60, recommendedMaxHours)
  return Math.max(4, Math.ceil((raw + 0.5) / 2) * 2)
}

export function hoursToPx(hours: number, maxHours: number): number {
  if (maxHours <= 0) return 0
  return (Math.max(0, hours) / maxHours) * BARS.plotHeight
}

export function minutesToPx(minutes: number, maxHours: number): number {
  return hoursToPx(minutes / 60, maxHours)
}

// y measured up from the plot baseline.
export function valueToY(minutes: number, maxHours: number): number {
  return BARS.topPad + BARS.plotHeight - minutesToPx(minutes, maxHours)
}

// Cap at ~7 x-axis labels so they never overlap.
export function labelStride(dayCount: number): number {
  return Math.max(1, Math.ceil(dayCount / 7))
}

// --- Drift chart geometry (24h clock on Y) ---
export const DRIFT = {
  plotHeight: 200,
  topPad: 12,
  bottomPad: 26,
  leftAxis: 34,
  dotRadius: 4,
} as const

export function driftSvgHeight(): number {
  return DRIFT.topPad + DRIFT.plotHeight + DRIFT.bottomPad
}

// "HH:mm" → minutes since midnight. Null on bad input.
export function clockToMinutes(clock: string | null | undefined): number | null {
  if (!clock) return null
  const match = /^(\d{2}):(\d{2})$/.exec(clock)
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

// Minutes since midnight → y (00:00 top, 24:00 bottom).
export function clockMinutesToY(minutes: number): number {
  return DRIFT.topPad + (minutes / 1440) * DRIFT.plotHeight
}

// Parse a "YYYY-MM-DD" DateOnly string as a local date (avoids UTC day shifts).
export function parseDateOnly(value: string): Date {
  const [y, m, d] = value.split("-").map(Number)
  return new Date(y, m - 1, d)
}

const dayLabelFormat = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" })

export function formatDayLabel(dateOnly: string): string {
  return dayLabelFormat.format(parseDateOnly(dateOnly))
}
